import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, OrderLineModel, SpecificationModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    specification?: SpecificationModel[];
    product: CartLineModel | ProductContextModel | OrderLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    showProductNumber?: boolean;
    productReelId?: string;
    productSiteId?: string;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    productLink?: LinkPresentationProps;
    tooltip?: InjectableCss;
    toolTipLabel?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: { gap: 5 },
    erpNumberLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    customerNameLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    customerNameValueText: {
        css: css`
            width: fit-content;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    manufacturerItemLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
        `,
    },
    manufacturerItemValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
        `,
    },
    productLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
    customerNameGridItem: {
        css: css`
            ul {
                margin-left: 16px;
                list-style: disc;
            }
        `,
    },
    tooltip: {
        css: css`
            cursor: pointer;
            margin-left: 10px;
            position: relative;
            display: flex;
            justify-content: center;
            color: #1e75ba;
            font-size: 13px;
            font-weight: 500;
            &:hover {
                span {
                    visibility: visible;
                    z-index: 2;
                }
            }
        `,
    },
    toolTipLabel: {
        css: css`
            visibility: hidden;
            width: 130px;
            background-color: #363636;
            color: #fff;
            text-align: center;
            padding: 12px;
            position: absolute;
            z-index: 1;
            top: 35px;
            text-align: inherit;
            p {
                height: 1px;
            }

            &::after {
                content: "";
                position: absolute;
                top: 100%;
                left: 50%;
                margin-left: -15px;
                border-width: 17px;
                border-style: solid;
                transform: rotate(180deg);
                top: -32px;
                border-color: #363636 transparent transparent transparent;
            }
        `,
    },
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    specification,
    product,
    onClick,
    showProductNumber = true,
    productReelId,
    productSiteId,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const [readMore, setReadMore] = useState(false);
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;
    const stripedHtml = specification ? specification[0]?.htmlContent : "";

    const characterCount = stripedHtml?.length ?? "";
    const subStripedHtml = stripedHtml?.substring(0, 120) ?? "";
    const linkName = "Read More";
    const productDetailPath = product
        ? "product" in product
            ? product.productInfo.productDetailPath
            : product.productUri
        : "";
    const splitCustomerProductNumber = customerProductNumber?.split(",") || [""];

    const shortDescription = (
        <Typography {...styles.erpNumberValueText} data-test-selector="productDescription">
            {subStripedHtml === "" ? subStripedHtml : `${subStripedHtml}...`}
            {characterCount > 120 ? (
                <Link
                    {...styles.productLink}
                    href={productDetailPath}
                    alt={`See product details for ${linkName}`}
                    onClick={onClick}
                    data-test-selector="productLink"
                >
                    {linkName}
                </Link>
            ) : null}
        </Typography>
    );

    return (
        <GridContainer {...styles.container}>
            {displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem}>
                    {showLabel && <Typography {...styles.manufacturerItemLabelText}>{translate("MFR #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MFR #")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                        {manufacturerItem}
                    </Typography>
                </GridItem>
            )}
            {showProductNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("SKU #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>{translate("SKU #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                        {productNumber}
                    </Typography>
                </GridItem>
            )}
            {displayCustomerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem}>
                    {showLabel && <Typography {...styles.customerNameLabelText}>{translate("PART #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>{translate("PART #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">
                        {splitCustomerProductNumber[0]}
                    </Typography>
                    {splitCustomerProductNumber[1] && (
                        <StyledWrapper {...styles.tooltip}>
                            Show More
                            <Typography data-test-selector="tool-Tip-Label" {...styles.toolTipLabel}>
                                <ul data-test-selector="customerProductNumber">
                                    {splitCustomerProductNumber.slice(1).map((part, index) => (
                                        <li key={index}>{part}</li>
                                    ))}
                                </ul>
                            </Typography>
                        </StyledWrapper>
                    )}
                </GridItem>
            )}
            {productReelId && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Ree lId #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>{translate("Reel Id #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="productReelNumber">
                        {productReelId}
                    </Typography>
                </GridItem>
            )}
            {productSiteId && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("Site Id #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.erpNumberLabelText}>{translate("Site Id #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.erpNumberValueText} data-test-selector="productSiteNumber">
                        {productSiteId}
                    </Typography>
                </GridItem>
            )}

            {specification && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                    {!readMore && shortDescription}
                </GridItem>
            )}
        </GridContainer>
    );
};

export default ProductPartNumbers;
